import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { EmployeesService } from '@app/services/employees.service';
import { of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import * as feather from 'feather-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface TPendingMvrRenewalsPayload {
  entities: number[];
  run_now: boolean;
  rule_name: string;
  rule_provider: string;
  entity_type: string;
  schedule_name: string;
  schedule_type: string;
}

interface TPendingIdvMvrPayload {
  entities: number[];
  run_now: boolean;
  rule_name: string;
  rule_provider: string;
  entity_type: string;
  schedule_name: string;
  schedule_type: string;
  flow_params?: {
    flow_type: string;
    trigger_mvr_on_success: boolean;
    schema: string;
  };
}

const DEFAULT_FILTERS = {
  name: '',
  dob: '',
  state: '',
  driver_license_number: '',
  status: '',
  request_type: ''
};

@Component({
  standalone: false,
  selector: 'app-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.css'],
})
export class EmployeesComponent implements OnInit {
  employees: any[] = [];
  loading = false;
  isLoading = false;
  file: File | null = null;
  pendingMvrRenewalsPayload: TPendingMvrRenewalsPayload | null = null;
  pendingIdvMvrPayload: TPendingIdvMvrPayload | null = null;
  errorMessage: string | null = null;
  page = 1;
  total = 0;
  pages = 0;
  currentPage = 1;
  perPage = 20;
  filters: any = DEFAULT_FILTERS;
  sortColumn = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private sanitizer: DomSanitizer,
    private employeesService: EmployeesService,
    private router: Router,
    private route: ActivatedRoute) { }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      console.log('Query Params:', params);
      this.fetchEmployees();
      this.setPendingRenewalsPayload();
      this.setPendingIdvMvrPayload();
    });
  }

  ngAfterViewInit() {
    feather.replace();
  }

  applyFilters() {
    console.log('filters applied:', this.filters);
    this.fetchEmployees();
  }
  
  resetState() {
    this.filters.name = '';
    this.filters.dob = '';
    this.filters.state = '';
    this.filters.driver_license_number = '';
    this.filters.status = '';
    this.filters.request_type = '';
  }

  clearFilters() {
    this.resetState();
    this.fetchEmployees();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pages) return;
    this.currentPage = page;
    this.fetchEmployees();
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.fetchEmployees();
  }

  getStatus(employee: any): SafeHtml {
    let html = '-';
    
    // Use the new 'status' field instead of 'mvr_status'
    const status = employee.status || employee.mvr_status;
    
    switch(status) {
      case 'PENDING':
        html = '<span style="color: #6c757d;">&#8987; PENDING</span>';
        break;
      case 'ID_VERIFICATION_IN_PROGRESS':
        html = '<span style="color: #17a2b8;">&#128274; ID VERIFICATION IN PROGRESS</span>';
        break;
      case 'MVR_IN_PROGRESS':
        html = '<span style="color: #007bff;">&#128665; MVR IN PROGRESS</span>';
        break;
      case 'IN_PROGRESS':
        html = '<span style="color: #ffc107;">&#8987; PROCESSING</span>';
        break;
      case 'COMPLETED':
        if (employee.assessment_decision == 'IN_PROC') {
          html = '<span style="color: #ffc107;">&#8987; REVIEWING</span>';
        } else if (['RECORD', 'HIT'].includes(employee.assessment_decision)) {
          html = '<span style="color: #dc3545;">&#10060; HIT</span>';
        } else if (['CLEAR', 'PASSED', 'NO_RECORD'].includes(employee.assessment_decision)) {
          html = '<span style="color: #28a745;">&#9989; CLEAR</span>';
        } else {
          html = '<span style="color: #28a745;">&#9989; COMPLETED</span>';
        }
        break;
      default:
        html = '<span style="color: #6c757d;">-</span>';
    }
    
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
  
  getRequestType(employee: any): string {
    return employee.request_type || '-';
  }
  
  getStatusBadgeClass(employee: any): string {
    const status = employee.status || employee.mvr_status;
    
    switch(status) {
      case 'PENDING':
        return 'badge badge-secondary';
      case 'ID_VERIFICATION_IN_PROGRESS':
        return 'badge badge-info';
      case 'MVR_IN_PROGRESS':
        return 'badge badge-primary';
      case 'IN_PROGRESS':
        return 'badge badge-warning';
      case 'COMPLETED':
        if (['RECORD', 'HIT'].includes(employee.assessment_decision)) {
          return 'badge badge-danger';
        } else if (['CLEAR', 'PASSED', 'NO_RECORD'].includes(employee.assessment_decision)) {
          return 'badge badge-success';
        }
        return 'badge badge-success';
      default:
        return 'badge badge-light';
    }
  }

  getStatusCount(status: string): number {
    if (!this.employees || this.employees.length === 0) {
      return 0;
    }
    return this.employees.filter(emp => {
      const empStatus = emp.status || emp.mvr_status;
      return empStatus === status;
    }).length;
  }

  onArchiveToggle(employee: any): void {
    if (employee.is_archive) {
      // Unarchive (Show)
      this.employeesService.unarchiveEmployee(employee.candidate_id).subscribe({
        next: () => {
          this.fetchEmployees();
        },
        error: (error) => {
          console.error('Error unarchiving employee:', error);
          this.errorMessage = 'Failed to unarchive employee';
        },
      });
    } else {
      // Archive (Clear)
      this.employeesService.archiveEmployee(employee.candidate_id).subscribe({
        next: () => {
          this.fetchEmployees();
        },
        error: (error) => {
          console.error('Error archiving employee:', error);
          this.errorMessage = 'Failed to archive employee';
        },
      });
    }
  }

  hasActiveFilters(): boolean {
    return Object.values(this.filters).some(value => value !== '' && value !== null && value !== undefined);
  }

  fetchEmployees() {
    this.loading = true;
    // Dynamically enable show_all when user is searching/filtering
    // This ensures the default view is clean, but search can find everything
    const showAll = this.hasActiveFilters();
    this.employeesService.loadEmployees(this.currentPage, this.perPage, this.filters, showAll, this.sortColumn, this.sortDirection).pipe(
      switchMap((response: any) => {
        console.log('Employees loaded successfully', response);
        this.total = response.total;

        // Backend handles filtering (IN_PROGRESS and Unknown request types)
        // when showAll is false, so we use the list directly.
        this.employees = response.list;

        this.pages = Math.ceil(this.total / this.perPage);
        return of(response);
      }),
      catchError((error) => {
        console.error('Failed to load employees', error);
        this.employees = [];
        return of([]);
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }

  viewEmployeeDetails(employeeCandidateId: string) {
    this.router.navigate(['/home/employees', employeeCandidateId]);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.file = input.files[0];
      console.log('Selected file:', this.file);
    } else {
      this.file = null;
      console.log('No file selected');
    }
  }

  uploadFile(): void {
    if (!this.file) {
      console.error('No file selected for upload');
      return;
    }
    this.employeesService.uploadFile(this.file).pipe(
      switchMap((response) => {
        console.log('File uploaded successfully', response);
        this.errorMessage = 'File uploaded successfully';
        document.location.reload();
        return of(response);
      }),
      catchError((error) => {
        console.error('File upload failed', error);
        this.errorMessage = 'File upload failed';
        return of(null);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe();
  }

  triggerPendingRenewals(): void {
    this.employeesService.triggerPendingMvrRenewals(this.pendingMvrRenewalsPayload).pipe(
      switchMap((response) => {
        console.log('Pending MVR renewals triggered successfully', response);
        this.errorMessage = 'Pending MVR renewals triggered successfully';
        // Reload the page to show updated employee list with MVR status
        setTimeout(() => {
          document.location.reload();
        }, 1000);
        return of(response);
      }),
      catchError((error) => {
        console.error('Failed to trigger pending MVR renewals', error);
        this.errorMessage = 'Failed to trigger pending MVR renewals';
        return of(null);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe();
  }

  setPendingRenewalsPayload(): any {
    console.log('running setPendingRenewalsPayload');
    this.employeesService.getPendingMvrRenewalsPayload().pipe(
      switchMap((response: TPendingMvrRenewalsPayload) => {
        console.log('Pending MVR renewals payload', response);
        this.pendingMvrRenewalsPayload = response;
        return of(response);
      }),
      catchError((error) => {
        console.error('Failed to get pending MVR renewals payload', error);
        return of(null);
      })
    ).subscribe();
  }

  setPendingIdvMvrPayload(): any {
    console.log('running setPendingIdvMvrPayload');
    this.employeesService.getPendingIdvMvrPayload().pipe(
      switchMap((response: TPendingIdvMvrPayload) => {
        console.log('Pending ID Verification + MVR payload', response);
        this.pendingIdvMvrPayload = response;
        return of(response);
      }),
      catchError((error) => {
        console.error('Failed to get pending ID Verification + MVR payload', error);
        return of(null);
      })
    ).subscribe();
  }

  triggerIdvMvrRenewals(): void {
    if (!this.pendingIdvMvrPayload) {
      console.error('No pending ID Verification + MVR payload available');
      return;
    }
    
    this.employeesService.triggerPendingMvrRenewals(this.pendingIdvMvrPayload).pipe(
      switchMap((response) => {
        console.log('ID Verification + MVR renewals triggered successfully', response);
        this.errorMessage = 'ID Verification + MVR process initiated successfully';
        // Reload the page to show updated employee list with ID+MVR status
        setTimeout(() => {
          document.location.reload();
        }, 1000);
        return of(response);
      }),
      catchError((error) => {
        console.error('Failed to trigger ID Verification + MVR renewals', error);
        this.errorMessage = 'Failed to trigger ID Verification + MVR renewals';
        return of(null);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe();
  }

  requestTypeClasses: Record<string, string> = {
    'ID Verification + MVR': 'badge badge-info',
    'Perpetual': 'badge badge-success',
    'MVR Only': 'badge badge-primary'
  };

}


